import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Button, Spinner, Alert, Table, Badge } from 'react-bootstrap'
import { recipeAPI } from '../api/recipeAPI'
import QuantityPicker, { isSuperUnit } from './QuantityPicker'
import QuantityDisplay from './QuantityDisplay'
import { useWarehouse } from '../contexts/WarehouseContext'
import { formatDateOnly } from '../utils/commonUtils'

const EXPIRATION_WARNING_DAYS = 90

function PickInventoryModal({
    show,
    onHide,
    ingredient,
    requiredQuantity,
    requiredWeight,
    theoreticalQuantity,
    theoreticalWeight,
    weightUnit,
    recipeId,
    onConfirm,
    existingAllocations
}) {
    const { currentWarehouse } = useWarehouse()
    const [inventoryItems, setInventoryItems] = useState([])
    const [allocations, setAllocations] = useState({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Item-family ingredients: quantities are in sub-units, each row has its own case_pack
    const isItemFamily = !!ingredient?.item_family
    const isWeightMode = requiredWeight != null

    // Fetch inventory when modal opens
    useEffect(() => {
        if (show && ingredient && recipeId) {
            fetchInventory()
        }
    }, [show, ingredient, recipeId])

    // Initialize allocations from existing when modal opens
    useEffect(() => {
        if (show && existingAllocations) {
            setAllocations(existingAllocations)
        } else if (show) {
            setAllocations({})
        }
    }, [show, existingAllocations])

    const fetchInventory = async () => {
        try {
            setLoading(true)
            setError(null)
            const warehouseId = currentWarehouse?._id || currentWarehouse?.id || null
            const data = await recipeAPI.getIngredientInventory(recipeId, ingredient._id, warehouseId)
            setInventoryItems(data || [])
        } catch (err) {
            console.error('Error fetching inventory:', err)
            setError('Failed to load available inventory')
        } finally {
            setLoading(false)
        }
    }

    // Helper: get case_pack for an inventory item's stock item
    const getItemCasePack = (item) => item.stock_item?.case_pack || 1

    // Helper: get weight per case for an inventory item (in lbs)
    const getItemWeightPerCase = (item) => {
        const si = item.stock_item
        if (!si?.item_size) return null
        const size = parseFloat(si.item_size)
        const casePack = si.case_pack || 1
        let weight = isSuperUnit(si.uom) ? size : size * casePack
        if (si.item_size_units === 'oz') weight /= 16
        return weight
    }

    // Convert weight between lbs and the ingredient's weight unit
    const toWeightUnit = (lbs) => {
        if (weightUnit === 'oz') return lbs * 16
        return lbs
    }
    const toLbs = (val) => {
        if (weightUnit === 'oz') return val / 16
        return val
    }

    // Calculate totals — in sub-units for item-family, cases for stock-item
    const totalPicked = useMemo(() => {
        if (isItemFamily) {
            // Convert each allocation (cases) to sub-units using per-item case_pack
            // Super-unit allocations are already in sub-units; don't multiply by case_pack
            return Object.entries(allocations).reduce((sum, [id, qty]) => {
                const item = inventoryItems.find(i => String(i._id) === String(id))
                const casePack = item ? getItemCasePack(item) : 1
                const itemUom = item?.stock_item?.uom || ''
                return sum + (parseFloat(qty) || 0) * (isSuperUnit(itemUom) ? 1 : casePack)
            }, 0)
        }
        return Object.values(allocations).reduce((sum, qty) => sum + (parseFloat(qty) || 0), 0)
    }, [allocations, isItemFamily, inventoryItems])

    const totalAvailable = useMemo(() => {
        if (isItemFamily) {
            // Sum inventory in sub-units
            // Super-unit inventory quantity is already in sub-units
            return inventoryItems.reduce((sum, item) => {
                const itemUom = item?.stock_item?.uom || ''
                return sum + parseFloat(item.quantity) * (isSuperUnit(itemUom) ? 1 : getItemCasePack(item))
            }, 0)
        }
        return inventoryItems.reduce((sum, item) => sum + parseFloat(item.quantity), 0)
    }, [inventoryItems, isItemFamily])

    // Weight totals for weight-mode ingredients
    const totalPickedWeight = useMemo(() => {
        if (!isWeightMode) return 0
        return Object.entries(allocations).reduce((sum, [id, qty]) => {
            const item = inventoryItems.find(i => String(i._id) === String(id))
            const wpc = item ? getItemWeightPerCase(item) : null
            if (wpc == null) return sum
            return sum + (parseFloat(qty) || 0) * wpc
        }, 0)
    }, [allocations, inventoryItems, isWeightMode])

    const totalAvailableWeight = useMemo(() => {
        if (!isWeightMode) return 0
        return inventoryItems.reduce((sum, item) => {
            const wpc = getItemWeightPerCase(item)
            if (wpc == null) return sum
            return sum + parseFloat(item.quantity) * wpc
        }, 0)
    }, [inventoryItems, isWeightMode])

    const difference = isWeightMode
        ? toWeightUnit(totalPickedWeight) - requiredWeight
        : totalPicked - requiredQuantity

    const hasRequired = isWeightMode ? requiredWeight > 0 : requiredQuantity > 0

    // Case pack and UOM for summary display
    // Item-family: casePack=1 (sub-unit totals displayed as plain numbers)
    // Stock-item: use ingredient's case_pack
    const summaryCasePack = ingredient?.case_pack || 1
    const summaryUom = ingredient?.unit_of_measure || ''
    const familySuffix = isItemFamily && !isSuperUnit(summaryUom) ? ' items' : ''

    // Check if item is expired or expiring soon
    const getExpirationStatus = (bbDate) => {
        if (!bbDate) return null
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const expDate = new Date(bbDate)
        expDate.setHours(0, 0, 0, 0)

        const daysUntilExpiration = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24))

        if (daysUntilExpiration < 0) return 'expired'
        if (daysUntilExpiration <= EXPIRATION_WARNING_DAYS) return 'warning'
        return null
    }

    const handleQuantityChange = (inventoryId, value) => {
        if (value === '' || value === null || value === undefined) {
            const newAllocations = { ...allocations }
            delete newAllocations[inventoryId]
            setAllocations(newAllocations)
            return
        }
        const numValue = parseFloat(value) || 0
        const item = inventoryItems.find(i => i._id === inventoryId)
        const maxQty = item ? parseFloat(item.quantity) : 0

        if (numValue <= 0) {
            const newAllocations = { ...allocations }
            delete newAllocations[inventoryId]
            setAllocations(newAllocations)
        } else {
            setAllocations({
                ...allocations,
                [inventoryId]: Math.min(numValue, maxQty)
            })
        }
    }

    const handleAutoPick = () => {
        const newAllocations = {}

        if (isWeightMode) {
            // Track remaining weight in lbs
            let remainingLbs = toLbs(requiredWeight)
            for (const item of inventoryItems) {
                if (remainingLbs <= 0) break
                if (item.in_production) continue
                const available = parseFloat(item.quantity)
                const wpc = getItemWeightPerCase(item)
                if (wpc == null || wpc <= 0) continue

                // Convert remaining weight to cases for this item
                let pickQty = Math.min(remainingLbs / wpc, available)

                // Round up fractional quantities to whole numbers
                if (pickQty > 0 && pickQty % 1 !== 0) {
                    const rounded = Math.ceil(pickQty)
                    if (rounded <= available) {
                        pickQty = rounded
                    }
                }
                if (pickQty > 0) {
                    newAllocations[item._id] = pickQty
                    remainingLbs -= pickQty * wpc
                }
            }
        } else if (isItemFamily) {
            // Track remaining in sub-units
            let remaining = requiredQuantity
            for (const item of inventoryItems) {
                if (remaining <= 0) break
                if (item.in_production) continue
                const available = parseFloat(item.quantity)
                const casePack = getItemCasePack(item)
                const itemUom = item?.stock_item?.uom || ''

                let pickQty
                if (isSuperUnit(itemUom)) {
                    // Super-unit: allocations in sub-units, pick directly
                    pickQty = Math.min(remaining, available)
                } else {
                    // Regular: convert remaining sub-units to cases
                    pickQty = Math.min(remaining / casePack, available)
                    // Round up fractional cases to whole numbers
                    if (pickQty > 0 && pickQty % 1 !== 0) {
                        const rounded = Math.ceil(pickQty)
                        if (rounded <= available) pickQty = rounded
                    }
                }
                if (pickQty > 0) {
                    newAllocations[item._id] = pickQty
                    remaining -= isSuperUnit(itemUom) ? pickQty : pickQty * casePack
                }
            }
        } else {
            // Stock-item ingredient: existing behavior
            let remaining = requiredQuantity
            for (const item of inventoryItems) {
                if (remaining <= 0) break
                if (item.in_production) continue
                const available = parseFloat(item.quantity)
                const casePack = ingredient?.case_pack || 1
                let pickQty = Math.min(remaining, available)

                // Round up fractional quantities to whole numbers
                if (pickQty > 0 && pickQty % 1 !== 0) {
                    const rounded = Math.ceil(pickQty)
                    if (rounded <= available) {
                        pickQty = rounded
                    }
                }
                if (pickQty > 0) {
                    newAllocations[item._id] = pickQty
                    remaining -= pickQty
                }
            }
        }

        setAllocations(newAllocations)
    }

    const handleClear = () => {
        setAllocations({})
    }

    const handleConfirm = () => {
        // Filter out zero allocations and create final allocation object
        const finalAllocations = {}
        for (const [id, qty] of Object.entries(allocations)) {
            if (qty > 0) {
                finalAllocations[id] = qty
            }
        }
        onConfirm(finalAllocations, totalPicked)
        onHide()
    }


    const ingredientName = ingredient?.ingredient_name ||
                          ingredient?.stock_item_name ||
                          ingredient?.item_family_name ||
                          'Unknown Ingredient'

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Pick Inventory - {ingredientName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Summary Bar */}
                <div className="d-flex justify-content-between align-items-center mb-3 p-3 border rounded">
                    {hasRequired && (() => {
                        const showTheoretical = isWeightMode
                            ? theoreticalWeight != null && Math.abs(theoreticalWeight - requiredWeight) >= 0.001
                            : theoreticalQuantity != null && Math.abs(theoreticalQuantity - requiredQuantity) >= 0.0001
                        return (
                            <div>
                                {showTheoretical && (
                                    <>
                                        <strong>Recipe Qty:</strong>{' '}
                                        {isWeightMode
                                            ? <span>{theoreticalWeight} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={theoreticalQuantity} />{familySuffix})</span>
                                            : <span><QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={theoreticalQuantity} />{familySuffix}</span>
                                        }
                                        <span className="mx-2">|</span>
                                    </>
                                )}
                                <strong>Required{showTheoretical ? ' (incl. waste)' : ''}:</strong>{' '}
                                {isWeightMode
                                    ? <span>{requiredWeight} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={requiredQuantity} />{familySuffix})</span>
                                    : <span><QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={requiredQuantity} />{familySuffix}</span>
                                }
                            </div>
                        )
                    })()}
                    <div>
                        <strong>Picked:</strong>{' '}
                        {isWeightMode
                            ? <span>{toWeightUnit(totalPickedWeight).toFixed(2)} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalPicked} />{familySuffix})</span>
                            : <span><QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalPicked} />{familySuffix}</span>
                        }
                    </div>
                    {hasRequired && (
                        <div>
                            {Math.abs(difference) < 0.01 ? (
                                <Badge bg="success">Exact Match</Badge>
                            ) : difference > 0 ? (
                                <Badge bg="warning" text="dark">Over by {isWeightMode
                                    ? <span>{difference.toFixed(2)} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalPicked - requiredQuantity} />{familySuffix})</span>
                                    : <span><QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={difference} />{familySuffix}</span>
                                }</Badge>
                            ) : (
                                <Badge bg="danger">Short by {isWeightMode
                                    ? <span>{Math.abs(difference).toFixed(2)} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={Math.abs(totalPicked - requiredQuantity)} />{familySuffix})</span>
                                    : <span><QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={Math.abs(difference)} />{familySuffix}</span>
                                }</Badge>
                            )}
                        </div>
                    )}
                    <div>
                        <strong>Total Available:</strong>{' '}
                        {isWeightMode
                            ? <span>{toWeightUnit(totalAvailableWeight).toFixed(2)} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalAvailable} />{familySuffix})</span>
                            : <span><QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalAvailable} />{familySuffix}</span>
                        }
                    </div>
                </div>

                {/* Warning Messages */}
                {hasRequired && isWeightMode && toWeightUnit(totalAvailableWeight) < requiredWeight && (
                    <Alert variant="warning" className="mb-3">
                        Insufficient inventory: Only {toWeightUnit(totalAvailableWeight).toFixed(2)} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalAvailable} />) available, but {requiredWeight} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={requiredQuantity} />) required.
                    </Alert>
                )}
                {hasRequired && !isWeightMode && totalAvailable < requiredQuantity && (
                    <Alert variant="warning" className="mb-3">
                        Insufficient inventory: Only <QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalAvailable} /> available, but <QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={requiredQuantity} /> required.
                    </Alert>
                )}

                {/* Action Buttons */}
                <div className="mb-3 d-flex justify-content-end">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="me-2"
                        onClick={handleClear}
                        disabled={loading || Object.keys(allocations).length === 0}
                    >
                        Clear
                    </Button>
                    {hasRequired && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAutoPick}
                            disabled={loading || inventoryItems.length === 0}
                        >
                            Auto Pick
                        </Button>
                    )}
                </div>

                {/* Inventory Table */}
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : inventoryItems.length === 0 ? (
                    <Alert variant="info">No inventory available for this ingredient.</Alert>
                ) : (
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table striped bordered hover size="sm">
                            <thead className="sticky-top bg-white">
                                <tr>
                                    {isItemFamily && <th>Stock Item</th>}
                                    <th>Location</th>
                                    <th>Vendor</th>
                                    <th>LOT</th>
                                    <th>Best Before</th>
                                    <th>Available</th>
                                    {isWeightMode && <th>Weight</th>}
                                    <th style={{ width: '250px' }}>Pick Qty</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inventoryItems.map((item) => {
                                    const expStatus = getExpirationStatus(item.bb_date)
                                    const isInProduction = item.in_production
                                    const rowClass = isInProduction ? 'table-secondary' :
                                                    expStatus === 'expired' ? 'table-danger' :
                                                    expStatus === 'warning' ? 'table-warning' : ''
                                    // Per-row case_pack: use item's own stock item for item-family
                                    const rowCasePack = isItemFamily ? getItemCasePack(item) : (ingredient?.case_pack || 1)
                                    const rowUom = isItemFamily ? (item.stock_item?.uom || '') : (ingredient?.unit_of_measure || '')
                                    return (
                                        <tr key={item._id} className={rowClass} style={isInProduction ? { opacity: 0.7 } : undefined}>
                                            {isItemFamily && (
                                                <td>{item.stock_item?.description || '-'}</td>
                                            )}
                                            <td>
                                                {item.warehouse_name} / {item.location_name}
                                                {isInProduction && (
                                                    <Badge bg="warning" text="dark" className="ms-2">In Production</Badge>
                                                )}
                                            </td>
                                            <td>{item.vendor_name || '-'}</td>
                                            <td>{item.lot || '-'}</td>
                                            <td>
                                                {formatDateOnly(item.bb_date)}
                                                {expStatus === 'expired' && (
                                                    <Badge bg="danger" className="ms-1">Expired</Badge>
                                                )}
                                            </td>
                                            <td><QuantityDisplay casePack={rowCasePack} uom={rowUom} value={item.quantity} /></td>
                                            {isWeightMode && (
                                                <td>
                                                    {(() => {
                                                        const wpc = getItemWeightPerCase(item)
                                                        if (wpc == null) return '-'
                                                        const totalWeight = parseFloat(item.quantity) * wpc
                                                        return `${toWeightUnit(totalWeight).toFixed(2)} ${weightUnit}`
                                                    })()}
                                                </td>
                                            )}
                                            <td>
                                                {isInProduction ? (
                                                    <span className="text-muted">-</span>
                                                ) : (
                                                    <QuantityPicker
                                                        casePack={rowCasePack}
                                                        uom={rowUom}
                                                        value={allocations[item._id] ?? ''}
                                                        onChange={(qty) => handleQuantityChange(item._id, qty)}
                                                        max={item.quantity}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {hasRequired && Math.abs(difference) >= 0.01 && totalPicked > 0 && (
                    <Alert variant={difference > 0 ? 'warning' : 'danger'} className="mb-0 me-auto py-1 px-2">
                        {difference > 0
                            ? <>Warning: Picking {isWeightMode
                                ? <span>{difference.toFixed(2)} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={totalPicked - requiredQuantity} />)</span>
                                : <QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={difference} />
                            } more than required</>
                            : <>Warning: Picking {isWeightMode
                                ? <span>{Math.abs(difference).toFixed(2)} {weightUnit} (<QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={Math.abs(totalPicked - requiredQuantity)} />)</span>
                                : <QuantityDisplay casePack={summaryCasePack} uom={summaryUom} value={Math.abs(difference)} />
                            } less than required</>
                        }
                    </Alert>
                )}
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="success"
                    onClick={handleConfirm}
                    disabled={loading}
                >
                    Confirm
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default PickInventoryModal
